import { Button } from '../components/Button';
import { Modal } from '../components/Modal';
import { Typography } from '../components/Typography';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { EServiceLine, updateSiteTypeAndIsWCJob } from '@/reducers/jbpGeneralSlice';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormProvider, useForm } from 'react-hook-form';
import { FormButton } from '@portal_components/components/FormButton';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useUserInfo } from '@/hooks/useUserInfo';
import { selectBookingReasons, selectSiteTypes, selectStandingDataLoaded } from '@/reducers/standingDataSlice';
import { contractTypeCodes } from '@/hooks/useJbpQuery';
import { useMemo } from 'react';

type SiteTypeUpdateFormValues = {
  siteType: string;
  bookingReason?: string;
};
interface siteTypeCodeData {
  siteTypeCode: string;
  siteTypeDescription: string;
}

interface SiteTypeUpdateModalProps {
  mpan: string;
  serviceLine: EServiceLine;
  closeModalFunction: any;
}

export const SiteTypeUpdateModal = (props: SiteTypeUpdateModalProps) => {
  const dispatch = useAppDispatch();
  const bookingReasons = useAppSelector(selectBookingReasons);
  const standingDataLoaded = useAppSelector(selectStandingDataLoaded);
  const { isInternalUser } = useUserInfo();
  const { site } = useAppSelector(state => state.jbp);
  const siteTypesAll = useAppSelector(selectSiteTypes);

  const siteTypes = useMemo<siteTypeCodeData[]>(() => {
    if (!siteTypesAll?.length) return [];

    const contractTypeId = Number(contractTypeCodes[props.serviceLine]);

    return siteTypesAll
      .filter(st => Number(st.contractTypeId) === contractTypeId)
      .map(st => ({
        siteTypeCode: st.siteTypeId,
        siteTypeDescription: st.siteTypeDescription
      }));
  }, [siteTypesAll, props.serviceLine]);

  const rootNodeAddress = `/bookJob/siteJobType/${props.mpan}/${props.serviceLine}`;

  const navigate = useNavigate();

  const handleSiteTypeConfirmation = ({ siteType, bookingReason }: SiteTypeUpdateFormValues) => {
    const isWCSiteType = siteType.indexOf('WC') != -1;
    dispatch(
      updateSiteTypeAndIsWCJob({
        updatedSiteType: siteType,
        isWCJob: isWCSiteType,
        bookingReason: bookingReason ? parseInt(bookingReason) : undefined
      })
    );

    navigate(rootNodeAddress);
  };

  const validationSchema = Yup.object().shape({
    siteType: Yup.string()
      .required('Site type is required')
      .oneOf(
        siteTypes.map(st => st.siteTypeCode),
        'Please select a valid site type'
      ),
    bookingReason: isInternalUser
      ? Yup.string()
          .required('Booking reason is required')
          .oneOf(
            (bookingReasons ?? []).map(br => `${br.bookingPurposeId}`),
            'Please select a valid booking reason'
          )
      : Yup.string()
  });

  const methods = useForm<SiteTypeUpdateFormValues>({
    values: {
      siteType: site?.siteType || '',
      bookingReason: ''
    },
    resolver: yupResolver<SiteTypeUpdateFormValues>(validationSchema as any)
  });

  const modalTitle = 'Pre Booking Confirmation';

  return (
    <Modal title={modalTitle}>
      <Typography content={`Before proceeding with the booking please confirm the site type from the available options`} className="mb-3" />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSiteTypeConfirmation)}>
          <FormDropdown<SiteTypeUpdateFormValues>
            title="Site Type"
            name="siteType"
            placeholder="Please confirm the site type for this booking"
            mandatory
            elementsForDisplay={siteTypes.map(siteType => ({
              title: siteType.siteTypeDescription,
              value: siteType.siteTypeCode
            }))}
          />
          {standingDataLoaded && isInternalUser && (
            <FormDropdown<SiteTypeUpdateFormValues>
              title="Reason for Booking"
              name="bookingReason"
              placeholder="Please confirm the reason for making this booking"
              mandatory
              elementsForDisplay={(bookingReasons ?? []).map(reason => ({
                title: reason.bookingPurposeDescription,
                value: `${reason.bookingPurposeId}`
              }))}
            />
          )}
          <div className="float-right mt-3">
            <Button
              primary={false}
              text="Cancel"
              className="mr-4"
              disabled={false}
              onClick={() => {
                props.closeModalFunction(false);
              }}
            />
            <FormButton primary type="submit" disabled={false}>
              Ok
            </FormButton>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
